import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthCard } from "@/components/ax/AuthCard";
import { Button } from "@/components/ax/Button";
import { useI18n } from "@/i18n";
import { useFlash } from "@/hooks/useFlash";
import { getPendingEmail, markVerified, resendVerification } from "@/lib/mockAuth";

const COOLDOWN = 60;
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function VerifikasiEmail() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [left, setLeft] = useState(COOLDOWN);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useFlash();
  const [error, setError] = useState("");
  const email = getPendingEmail();

  // MOCK: ?mock=verified menggantikan klik tautan di email.
  useEffect(() => {
    if (params.get("mock") === "verified" && markVerified()) navigate("/onboarding", { replace: true });
  }, [params, navigate]);

  useEffect(() => {
    if (left <= 0) return undefined;
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);

  const resend = async () => {
    if (busy || left > 0) return;
    setBusy(true);
    setError("");
    setFlash("");
    try {
      const res = await resendVerification();
      if (res?.ok === true) {
        setLeft(COOLDOWN);
        setFlash(t("verify.resent"));
      } else {
        if (res?.error === "cooldown") setLeft(res.retryAfterSec || COOLDOWN);
        const key = res?.error === "unauthenticated" ? "verify.signinRequired" :
          res?.error === "verified" ? "verify.alreadyVerified" : res?.error === "cooldown" ? "verify.wait" : "verify.failed";
        setError(t(key));
      }
    } catch { setError(t("verify.failed")); }
    finally { setBusy(false); }
  };

  return (
    <AuthCard title={t("verify.title")} flash={flash} testId="verifikasi-page">
      <p className="m-0 text-base text-ax-text-2" data-testid="verify-body">
        {email ? t("verify.body", { email }) : t("verify.bodyAnon")}
      </p>
      {error && <p role="alert" data-testid="verify-error" className="mt-3 text-sm leading-5 text-ax-error">{error}</p>}
      <Link
        to="/masuk"
        data-testid="verify-signin-link"
        className="mt-4 inline-flex min-h-[48px] items-center text-sm font-medium text-ax-link hover:underline"
      >
        {t("verify.signinAction")}
      </Link>
      <Button
        variant="ghost"
        onClick={resend}
        disabled={left > 0 || busy}
        busy={busy}
        data-testid="resend-button"
        className="mt-4 gap-2 px-0"
      >
        {t("verify.resend")}
        {left > 0 && (
          <span className="font-mono text-[13px]" data-testid="resend-countdown">
            {mmss(left)}
          </span>
        )}
      </Button>
    </AuthCard>
  );
}
